import React from "react";
import { motion } from "framer-motion";
import { Sparkles } from "lucide-react";
import { ASSETS } from "../../data/landing";
import { useLanguage } from "../../i18n/LanguageContext";
import StoreButtons from "./StoreButtons";

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] },
  }),
};

export const About = () => {
  const { t } = useLanguage();
  const a = t.about;

  return (
    <section id="about" className="relative py-24 md:py-32" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 items-center gap-14 lg:grid-cols-[1.05fr_0.95fr] lg:gap-20">
          <div>
            <motion.span
              variants={fadeUp}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              className="text-xs font-bold uppercase tracking-[0.16em] text-[#7B1113]"
            >
              {a.label}
            </motion.span>
            <motion.h2
              variants={fadeUp}
              custom={1}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              className="mt-4 text-3xl font-medium leading-tight tracking-tight text-[#2A1112] md:text-4xl lg:text-5xl"
              data-testid="about-heading"
            >
              {a.heading}
            </motion.h2>

            <motion.div
              variants={fadeUp}
              custom={2}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              className="mt-7 space-y-5 text-base leading-relaxed text-[#5E4646] md:text-lg"
            >
              <p>{a.p1}</p>
              <p>
                {a.p2pre}
                <span className="font-semibold text-[#2A1112]">{a.p2strong}</span>
                {a.p2rest}
              </p>
            </motion.div>

            <motion.div
              variants={fadeUp}
              custom={3}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              className="mt-8"
            >
              <p className="mb-4 text-sm font-medium text-[#5E4646]">
                {a.topicsLabel}
              </p>
              <ul className="flex flex-wrap gap-2.5" data-testid="about-topics">
                {a.topics.map((topic, i) => (
                  <li
                    key={i}
                    data-testid={`about-topic-${i}`}
                    className="inline-flex items-center gap-1.5 rounded-full border border-[#7B1113]/12 bg-white px-4 py-2 text-sm font-medium text-[#2A1112] shadow-[0_6px_16px_-10px_rgba(123,17,19,0.25)] transition-colors duration-300 hover:border-[#F5A623] hover:text-[#7B1113]"
                  >
                    <Sparkles className="h-3.5 w-3.5 text-[#F5A623]" />
                    {topic}
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-base font-medium text-[#2A1112]">
                {a.closing}
              </p>
            </motion.div>

            <motion.div
              variants={fadeUp}
              custom={4}
              initial="hidden"
              whileInView="show"
              viewport={{ once: true, margin: "-80px" }}
              className="mt-9"
            >
              <StoreButtons testidPrefix="about" />
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative mx-auto w-full max-w-sm"
          >
            <motion.div
              animate={{ y: [-10, 10, -10] }}
              transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            >
              <div className="pointer-events-none absolute -inset-6 rounded-[3rem] bg-[#F5A623]/15 blur-3xl" />
              <div className="relative rounded-[2.75rem] bg-[#2A1112] p-2.5 shadow-[0_50px_90px_-30px_rgba(123,17,19,0.5)]">
                <div className="absolute left-1/2 top-3 z-10 h-5 w-24 -translate-x-1/2 rounded-full bg-[#2A1112]" />
                <div className="overflow-hidden rounded-[2.25rem] bg-white">
                  <img
                    src={ASSETS.screenSaathi}
                    alt="Saathi companion screen"
                    className="block w-full"
                    loading="lazy"
                    draggable={false}
                  />
                </div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default About;
